import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Target,
  Building2,
  Check,
  X as XIcon,
  Menu,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import {
  COACH_FEATURES,
  BROKER_FEATURES,
  TECH_STACK_COMPARISON,
  HOW_IT_WORKS_STEPS,
  PRICING_TIERS,
} from "./constants.js";

// CSS Styles injected as a component
const Styles = () => (
  <style>{`
    /* Premium Landing Page Styles */
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes slideInRight {
      from { opacity: 0; transform: translateX(30px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes float {
      0% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
      100% { transform: translateY(0px); }
    }
    @keyframes pulse-glow {
      0%, 100% { box-shadow: 0 0 20px rgba(58, 125, 255, 0.3); }
      50% { box-shadow: 0 0 40px rgba(58, 125, 255, 0.6); }
    }

    .animate-fade-in-up { animation: fadeInUp 0.8s ease-out forwards; }
    .animate-fade-in { animation: fadeIn 1s ease-out forwards; }
    .animate-slide-in-right { animation: slideInRight 0.8s ease-out forwards; }
    .animate-float { animation: float 4s ease-in-out infinite; }

    .gradient-text-animated {
      background: linear-gradient(270deg, #3A7DFF, #45D483, #3A7DFF);
      background-size: 400% 400%;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: gradient 3s ease infinite;
    }

    @keyframes gradient {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    .premium-card {
      background: linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(58, 125, 255, 0.1);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .premium-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 20px 40px rgba(58, 125, 255, 0.15);
      border-color: rgba(58, 125, 255, 0.3);
    }

    .glass-effect {
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(12px);
      border: 1px solid rgba(255, 255, 255, 0.5);
    }

    .btn-premium {
      position: relative;
      overflow: hidden;
      transition: all 0.3s ease;
    }
    .btn-premium:hover { transform: translateY(-2px); }
    .btn-premium::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 0;
      height: 0;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.3);
      transform: translate(-50%, -50%);
      transition: width 0.6s, height 0.6s;
    }
    .btn-premium:hover::before { width: 300px; height: 300px; }

    .pricing-glow::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      padding: 2px;
      background: linear-gradient(135deg, #3A7DFF, #45D483);
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      opacity: 0.5;
    }
    .pricing-glow:hover::before { opacity: 1; }

    .roadmap-item { position: relative; padding-left: 32px; }
    .roadmap-item::before {
      content: '';
      position: absolute;
      left: 8px;
      top: 8px;
      width: 2px;
      height: calc(100% + 16px);
      background: linear-gradient(180deg, #3A7DFF 0%, #45D483 100%);
    }
    .roadmap-item:last-child::before { height: 0; }

    .cta-pulse { animation: pulse-glow 2s ease-in-out infinite; }

    /* Chat Bubble Tail */
    .chat-tail-left::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: -6px;
      width: 12px;
      height: 12px;
      background: white;
      border-bottom-right-radius: 100%;
      z-index: -1;
    }
  `}</style>
);

const LandingPageNew = () => {
  const [persona, setPersona] = useState("coach");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".section-fade").forEach((el) => {
      if (el.id) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const content = {
    coach: {
      badge: "For Productivity Coaches",
      headline: "Scale Your Coaching Business",
      subheadline:
        "Stop drowning in spreadsheets. HelloPC is the systematized sidekick that tracks and organizes your agents so you can focus on changing lives.",
      features: COACH_FEATURES,
      ctaPrimary: "Join the Free Beta",
      ctaSecondary: "See How It Works",
    },
    broker: {
      badge: "For Brokerage Leaders",
      headline: "Systemize New Agent Success",
      subheadline:
        "Don't rely on luck. HelloPC gives you a repeatable system and transparency into your productivity coaching program.",
      features: BROKER_FEATURES,
      ctaPrimary: "Join the Free Beta",
      ctaSecondary: "See How It Works",
    },
  };

  const current = content[persona];

  return (
    <div className="bg-[#F9FAFB] text-[#2E2E38] font-sans min-h-screen flex flex-col overflow-x-hidden">
      <Styles />

      {/* Navigation */}
      <nav
        id="main-nav"
        className="fixed top-0 w-full z-50 transition-all duration-300 bg-white/80 backdrop-blur-md border-b border-slate-100"
      >
        <div className="mx-auto max-w-7xl px-6 h-20 flex items-center justify-between">
          <div
            id="nav-logo"
            className="flex items-center gap-1 text-2xl font-bold tracking-tight cursor-pointer"
          >
            <span className="text-[#3A7DFF]">Hello</span>
            <span className="text-[#45D483]">PC</span>
          </div>

          {/* Desktop Nav Actions */}
          <div className="hidden md:flex items-center gap-6">
            <a
              href="#how-it-works"
              className="text-sm font-medium text-slate-600 hover:text-[#3A7DFF] transition"
            >
              How It Works
            </a>
            <a
              href="#features"
              className="text-sm font-medium text-slate-600 hover:text-[#3A7DFF] transition"
            >
              Features
            </a>
            <a
              href="#pricing"
              className="text-sm font-medium text-slate-600 hover:text-[#3A7DFF] transition"
            >
              Pricing
            </a>
            <div className="h-5 w-px bg-slate-200"></div>
            <Link
              id="desktop-agent-sign-in-link"
              to="/agent/login"
              className="text-sm font-semibold text-[#3A7DFF] hover:text-[#2563EB] transition"
            >
              Agent sign in
            </Link>
            <Link
              id="desktop-coach-sign-in-link"
              to="/login"
              className="text-sm font-semibold text-slate-700 hover:text-[#2563EB] transition"
            >
              Coach sign in
            </Link>
            <Link
              to="/coach-signup"
              className="inline-flex items-center rounded-full bg-[#3A7DFF] px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-[#3A7DFF]/20 transition hover:bg-[#2563EB] hover:scale-105 active:scale-95"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            id="mobile-navigation-toggle"
            type="button"
            className="md:hidden p-2 text-slate-600 hover:bg-slate-50 rounded-lg"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation-menu"
          >
            {isMobileMenuOpen ? <XIcon size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div id="mobile-navigation-menu" className="absolute top-full left-0 w-full bg-white border-b border-slate-100 shadow-xl p-4 flex flex-col gap-2 md:hidden animate-fade-in">
            <a
              href="#how-it-works"
              className="p-3 rounded-lg hover:bg-slate-50 font-medium text-slate-600"
            >
              How It Works
            </a>
            <a
              href="#features"
              className="p-3 rounded-lg hover:bg-slate-50 font-medium text-slate-600"
            >
              Features
            </a>
            <a
              href="#pricing"
              className="p-3 rounded-lg hover:bg-slate-50 font-medium text-slate-600"
            >
              Pricing
            </a>
            <hr className="my-2 border-slate-100" />
            <Link
              id="mobile-agent-sign-in-link"
              to="/agent/login"
              className="min-h-11 p-3 rounded-lg text-[#3A7DFF] font-bold text-center"
            >
              Agent sign in
            </Link>
            <Link
              id="mobile-coach-sign-in-link"
              to="/login"
              className="min-h-11 p-3 rounded-lg text-slate-700 font-bold text-center"
            >
              Coach sign in
            </Link>
            <Link
              to="/coach-signup"
              className="p-3 rounded-lg bg-[#3A7DFF] text-white font-bold text-center"
            >
              Get Started
            </Link>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <header id="hero-section" className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-[#3A7DFF]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 w-[600px] h-[600px] bg-[#45D483]/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left Column: Content & Persona Toggle */}
            <div className="lg:col-span-6 text-center lg:text-left animate-fade-in-up">
              {/* Persona Switcher */}
              <div className="inline-flex bg-white p-1.5 rounded-full border border-slate-200 shadow-sm mb-8 mx-auto lg:mx-0">
                <button
                  onClick={() => setPersona("coach")}
                  className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 flex items-center gap-2 ${
                    persona === "coach"
                      ? "bg-[#3A7DFF] text-white shadow-md"
                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Target size={16} />
                  I'm a Coach
                </button>
                <button
                  onClick={() => setPersona("broker")}
                  className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 flex items-center gap-2 ${
                    persona === "broker"
                      ? "bg-[#45D483] text-white shadow-md"
                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Building2 size={16} />
                  I'm a Brokerage Leader
                </button>
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold tracking-tight text-[#2E2E38] mb-6 leading-[1.1]">
                The Operating System for{" "}
                <span className="gradient-text-animated">
                  {persona === "coach" ? "Productivity Coaching" : "Productivity Coaching"}
                </span>
                .
              </h1>

              <p className="text-xl text-[#2E2E38]/70 mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {current.subheadline}
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start mb-10">
                <Link
                  to="/coach-signup"
                  className="w-full sm:w-auto inline-flex items-center justify-center rounded-full bg-[#3A7DFF] px-8 py-4 text-base font-bold text-white shadow-lg shadow-[#3A7DFF]/30 transition hover:bg-[#2563EB] hover:-translate-y-1 cta-pulse"
                >
                  {current.ctaPrimary}
                </Link>
                <a
                  href="#how-it-works"
                  className="w-full sm:w-auto inline-flex items-center justify-center rounded-full border-2 border-slate-200 bg-white px-8 py-4 text-base font-semibold text-[#2E2E38] transition hover:border-[#3A7DFF] hover:text-[#3A7DFF]"
                >
                  {current.ctaSecondary}
                </a>
              </div>

              <div className="flex items-center justify-center lg:justify-start gap-6 text-sm font-medium text-slate-500">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`w-8 h-8 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-xs overflow-hidden`}
                      >
                        <img
                          src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${
                            i * 123
                          }&backgroundColor=b6e3f4`}
                          alt="User"
                        />
                      </div>
                    ))}
                  </div>
                  <span>Trusted by PCs Everywhere</span>
                </div>
                <div className="w-px h-4 bg-slate-300"></div>
                <div className="flex items-center gap-1">
                  <CheckCircle size={16} className="text-[#45D483]" />
                  <span>100% Free During Beta</span>
                </div>
              </div>
            </div>

            {/* Right Column: Product Visualization */}
            <div className="lg:col-span-6 relative mt-10 lg:mt-0 animate-slide-in-right">
              {/* Main Interface Container - Agent Detail View */}
              <div className="relative z-10 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden max-w-lg mx-auto lg:ml-auto animate-float">
                {/* Mock Window Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold">
                      SJ
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">
                        {persona === "coach" ? "Sarah Jenkins" : "Your Dashboard"}
                      </div>
                      <div className="text-xs text-blue-100">
                        {persona === "coach" ? "Active Agent" : "Agent Portal"}
                      </div>
                    </div>
                  </div>
                  {/* <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                    <span className="text-xs text-white">Online</span>
                  </div> */}
                </div>

                {/* Dynamic Content Body */}
                <div className="p-4 bg-gradient-to-b from-white to-slate-50/50 h-[420px] overflow-y-auto">
                  {persona === "coach" ? (
                    <>
                      {/* Performance Metrics - Coach View */}
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Performance Summary</h4>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-3 rounded-lg border border-emerald-200">
                            <div className="text-xs text-emerald-600 mb-1">Closings</div>
                            <div className="text-2xl font-bold text-emerald-700">3</div>
                            <div className="text-[10px] text-emerald-600 mt-1">Last: 2 days ago</div>
                          </div>
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg border border-blue-200">
                            <div className="text-xs text-blue-600 mb-1">Classes</div>
                            <div className="text-2xl font-bold text-blue-700">12</div>
                            <div className="text-[10px] text-blue-600 mt-1">Attended</div>
                          </div>
                          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-3 rounded-lg border border-purple-200">
                            <div className="text-xs text-purple-600 mb-1">Communications</div>
                            <div className="text-2xl font-bold text-purple-700">24</div>
                            <div className="text-[10px] text-purple-600 mt-1">Last: 1 hour ago</div>
                          </div>
                          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-3 rounded-lg border border-indigo-200">
                            <div className="text-xs text-indigo-600 mb-1">Engagement</div>
                            <div className="text-2xl font-bold text-indigo-700">87%</div>
                            <div className="text-[10px] text-indigo-600 mt-1">Score</div>
                          </div>
                        </div>
                      </div>

                      {/* Recent Activity */}
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Recent Activity</h4>
                        <div className="space-y-2">
                          <div className="bg-white border border-gray-200 rounded-lg p-2.5 shadow-sm">
                            <div className="flex items-center justify-between mb-1">
                              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
                                Call
                              </span>
                              <span className="text-[9px] text-gray-400">2h ago</span>
                            </div>
                            <p className="text-xs font-medium text-gray-900">Follow-up with buyer lead</p>
                            <p className="text-[10px] text-gray-500 mt-0.5">Scheduled showing for next week</p>
                          </div>
                          <div className="bg-white border border-gray-200 rounded-lg p-2.5 shadow-sm">
                            <div className="flex items-center justify-between mb-1">
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                                Task Completed
                              </span>
                              <span className="text-[9px] text-gray-400">5h ago</span>
                            </div>
                            <p className="text-xs font-medium text-gray-900">Daily prospecting calls</p>
                            <p className="text-[10px] text-gray-500 mt-0.5">Completed 15 calls</p>
                          </div>
                        </div>
                      </div>

                      {/* To-Do Preview */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Current Tasks</h4>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                            <span className="text-[10px] font-semibold text-amber-700 uppercase">Due Today</span>
                          </div>
                          <p className="text-xs font-medium text-gray-900 mb-1">Send follow-up emails to 10 prospects</p>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                              <div className="bg-amber-500 h-1.5 rounded-full" style={{ width: "60%" }}></div>
                            </div>
                            <span className="text-[9px] text-gray-600">6/10</span>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Agent Portal View */}
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Weekly Progress</h4>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">Onboarding Plan</p>
                              <p className="text-xs text-gray-600">Week 3 • 30 day timeline</p>
                            </div>
                            <div className="text-right">
                              <div className="text-xl font-bold text-blue-600">75%</div>
                              <div className="text-[10px] text-gray-600">Complete</div>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-600 h-2 rounded-full" style={{ width: "75%" }}></div>
                          </div>
                        </div>
                      </div>

                      {/* Your Tasks */}
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Your Tasks</h4>
                        <div className="space-y-2">
                          <div className="bg-white border border-gray-200 rounded-lg p-2.5 shadow-sm">
                            <div className="flex items-center gap-2 mb-1.5">
                              <div className="w-4 h-4 rounded border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                                <Check size={10} className="text-emerald-500" />
                              </div>
                              <p className="text-xs font-medium text-gray-900 line-through">Complete CRM training module</p>
                            </div>
                          </div>
                          <div className="bg-white border border-amber-200 rounded-lg p-2.5 shadow-sm">
                            <div className="flex items-center gap-2 mb-1.5">
                              <div className="w-4 h-4 rounded border-2 border-gray-300 flex-shrink-0"></div>
                              <p className="text-xs font-medium text-gray-900">Practice objection handling</p>
                            </div>
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700 ml-6">
                              Due Today
                            </span>
                          </div>
                          <div className="bg-white border border-gray-200 rounded-lg p-2.5 shadow-sm">
                            <div className="flex items-center gap-2 mb-1.5">
                              <div className="w-4 h-4 rounded border-2 border-gray-300 flex-shrink-0"></div>
                              <p className="text-xs font-medium text-gray-900">Shadow experienced agent</p>
                            </div>
                            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700 ml-6">
                              This Week
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Metrics */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">This Week</h4>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="bg-white border border-gray-200 rounded-lg p-2 text-center">
                            <div className="text-lg font-bold text-gray-900">8</div>
                            <div className="text-[9px] text-gray-500">Tasks Done</div>
                          </div>
                          <div className="bg-white border border-gray-200 rounded-lg p-2 text-center">
                            <div className="text-lg font-bold text-gray-900">2</div>
                            <div className="text-[9px] text-gray-500">Classes</div>
                          </div>
                          <div className="bg-white border border-gray-200 rounded-lg p-2 text-center">
                            <div className="text-lg font-bold text-gray-900">94%</div>
                            <div className="text-[9px] text-gray-500">Score</div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Floating Achievement Badge */}
              {/* <div
                className="absolute -bottom-5 -left-5 w-48 p-3 bg-white rounded-xl shadow-lg border border-emerald-200 transform -rotate-3 z-20 flex items-center gap-3 animate-bounce"
                style={{ animationDuration: "3s" }}
              >
                <div className="bg-emerald-100 p-2 rounded-full text-emerald-600">
                  <CheckCircle size={16} />
                </div>
                <div>
                  <div className="text-xs font-bold text-gray-700">
                    {persona === "coach" ? "Agent Milestone" : "Achievement Unlocked"}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {persona === "coach" ? "100 contacts made!" : "Week completed 🎉"}
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-grow">
        {/* How It Works */}
        <section
          id="how-it-works"
          className="py-20 bg-white section-fade relative"
        >
          <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-30 pointer-events-none"></div>
          <div className="mx-auto max-w-6xl px-6 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="inline-block px-3 py-1 rounded-full bg-[#3A7DFF]/10 text-[#3A7DFF] font-bold text-xs uppercase tracking-wider mb-4">
                Workflow
              </div>
              <h2 className="text-3xl font-bold text-[#2E2E38] sm:text-4xl mb-4">
                From Chaos to Clarity in 4 Steps
              </h2>
              <p className="text-lg text-[#2E2E38]/70">
                HelloPC handles the admin grind so you can focus on coaching.
              </p>
            </div>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {HOW_IT_WORKS_STEPS.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.step} className="relative text-center group">
                    <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-white border border-slate-200 shadow-sm text-[#3A7DFF] group-hover:bg-[#3A7DFF] group-hover:text-white group-hover:shadow-lg group-hover:shadow-[#3A7DFF]/30 transition-all duration-300 transform group-hover:-translate-y-1">
                      <Icon size={32} />
                    </div>
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#2E2E38] text-white text-xs font-bold px-2.5 py-1 rounded-full border-2 border-white">
                      {step.step}
                    </div>
                    <h3 className="text-lg font-bold text-[#2E2E38] mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-[#2E2E38]/70 leading-relaxed">
                      {step.description}
                    </p>
                    {index < HOW_IT_WORKS_STEPS.length - 1 && (
                      <div className="hidden lg:block absolute top-8 -right-4 text-slate-200">
                        <ArrowRight size={24} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-24 bg-[#F9FAFB] section-fade">
          <div className="mx-auto max-w-6xl px-6">
            <div className="grid lg:grid-cols-12 gap-12">
              <div className="lg:col-span-5">
                <h2 className="text-3xl font-bold text-[#2E2E38] sm:text-4xl mb-6 leading-tight">
                  {persona === "coach"
                    ? "Your AI Co-Pilot That Never Forgets a Detail"
                    : "The Infrastructure for Scalable Coaching"}
                </h2>
                <p className="text-lg text-[#2E2E38]/70 leading-relaxed mb-8">
                  {persona === "coach"
                    ? "HelloPC keeps agent relationships warm and productive. We automate the grind so you can focus on what you do best: coaching humans."
                    : "HelloPC gives you a repeatable system that onboards agents consistently, tracks accountability automatically, and scales without adding headcount."}
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#45D483]/10 flex items-center justify-center text-[#45D483]">
                      <Check size={20} />
                    </div>
                    <span className="font-medium text-slate-700">
                      Automated Accountability Alerts
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#45D483]/10 flex items-center justify-center text-[#45D483]">
                      <Check size={20} />
                    </div>
                    <span className="font-medium text-slate-700">
                      Conversation Memory & CRM
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#45D483]/10 flex items-center justify-center text-[#45D483]">
                      <Check size={20} />
                    </div>
                    <span className="font-medium text-slate-700">
                      Weekly Growth Reports
                    </span>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
                {current.features.map((feature) => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={feature.title}
                      className="group rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm transition premium-card hover:border-[#3A7DFF]/30 flex flex-col"
                    >
                      <div className="mb-4 w-12 h-12 rounded-xl bg-[#3A7DFF]/5 text-[#3A7DFF] flex items-center justify-center group-hover:bg-[#3A7DFF] group-hover:text-white transition-colors">
                        <Icon size={24} />
                      </div>
                      <h3 className="text-lg font-bold text-[#2E2E38] mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-[#2E2E38]/70 leading-relaxed mt-auto">
                        {feature.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Tech Stack Comparison */}
        <section className="py-20 bg-[#2E2E38] text-white section-fade overflow-hidden relative">
          {/* Background shapes */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-10">
            <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-[#3A7DFF] blur-3xl"></div>
            <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full bg-[#45D483] blur-3xl"></div>
          </div>

          <div className="mx-auto max-w-6xl px-6 relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold sm:text-4xl mb-4">
                Replace Your Entire Coaching Tech Stack
              </h2>
              <p className="text-lg text-white/80">
                Stop paying for (and managing) 5 different tools. HelloPC does
                it all.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 items-center">
              {/* The Old Way */}
              <div className="lg:col-span-2 space-y-4">
                {TECH_STACK_COMPARISON.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 bg-white/5 backdrop-blur-sm p-4 rounded-xl border border-white/10 hover:bg-white/10 transition-colors"
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 font-bold">
                      <XIcon size={16} />
                    </div>
                    <div className="flex-grow">
                      <div className="flex justify-between items-center mb-1">
                        <h4 className="font-semibold text-white">
                          {item.tool}
                        </h4>
                        <span className="text-xs font-medium bg-white/10 px-2 py-1 rounded text-white/70 border border-white/10">
                          {item.cost}
                        </span>
                      </div>
                      <p className="text-sm text-white/60">{item.problem}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* The HelloPC Way */}
              <div className="lg:col-span-1">
                <div className="bg-white text-[#2E2E38] rounded-3xl p-8 shadow-2xl transform lg:scale-110 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#3A7DFF] to-[#45D483]"></div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#3A7DFF] to-[#45D483] text-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#3A7DFF]/30">
                      <Check size={32} strokeWidth={3} />
                    </div>
                    <h3 className="text-2xl font-bold text-[#2E2E38] mb-1">
                      HelloPC
                    </h3>
                    <div className="text-4xl font-bold text-[#45D483] mb-2">
                      Free
                      <span className="text-lg text-slate-400 font-medium ml-1">
                        during Beta
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 mb-6">
                      One system. Zero spreadsheets. Zero cost.
                    </p>
                    <Link
                      to="/coach-signup"
                      className="block w-full bg-[#45D483] hover:bg-[#22C55E] text-white font-bold py-3.5 rounded-xl transition-colors shadow-lg"
                    >
                      Get Free Access
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-24 bg-[#F9FAFB] section-fade">
          <div className="mx-auto max-w-6xl px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 rounded-full bg-[#45D483]/10 text-[#45D483] font-bold text-sm uppercase tracking-wider mb-4">
                Limited Time — Free Beta Access
              </div>
              <h2 className="text-3xl font-bold text-[#2E2E38] sm:text-4xl mb-4">
                Free for Beta Users
              </h2>
              <p className="text-lg text-[#2E2E38]/70">
                Get full access at no cost while we build together. No credit card required.
              </p>
            </div>
            <div className="grid gap-8 lg:grid-cols-3 items-start">
              {PRICING_TIERS.map((tier) => (
                <div
                  key={tier.name}
                  className={`relative rounded-3xl p-8 transition-all duration-300 flex flex-col h-full ${
                    tier.highlighted
                      ? "bg-white border-2 border-[#45D483] shadow-xl pricing-glow transform lg:-translate-y-4 z-10"
                      : "bg-white border border-[#E2E8F0] hover:border-[#3A7DFF]/30 hover:shadow-lg"
                  } ${!tier.available ? "opacity-90 grayscale-[0.5]" : ""}`}
                >
                  {tier.badge && (
                    <div
                      className={`absolute right-6 top-6 rounded-full px-3 py-1 text-xs font-bold text-white ${
                        tier.comingSoon ? "bg-[#FF6F61]" : "bg-[#45D483]"
                      }`}
                    >
                      {tier.badge}
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-[#2E2E38]">
                    {tier.name}
                  </h3>
                  <p className="text-xs font-bold text-[#3A7DFF] uppercase tracking-wider mt-1">
                    {tier.tagline}
                  </p>

                  <div className="mt-6 flex items-baseline">
                    <span className="text-4xl font-bold text-[#2E2E38]">
                      {tier.price}
                    </span>
                    <span className="text-sm text-[#2E2E38]/60 ml-1">
                      {tier.period}
                    </span>
                  </div>

                  <p className="mt-4 text-sm text-[#2E2E38]/70 italic border-l-2 border-[#3A7DFF]/20 pl-3">
                    "{tier.description}"
                  </p>

                  <div className="my-6 border-t border-[#E2E8F0]"></div>

                  <ul className="space-y-3 mb-8 flex-grow">
                    {tier.features.map((feature, fIndex) => (
                      <li
                        key={fIndex}
                        className={`flex items-start gap-2 text-sm ${
                          feature.includes("Everything in")
                            ? "font-bold text-[#2E2E38]"
                            : "text-[#2E2E38]/80"
                        }`}
                      >
                        {!feature.includes("Everything in") && (
                          <Check
                            size={16}
                            className="text-[#45D483] flex-shrink-0 mt-0.5"
                          />
                        )}
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={tier.available ? "/coach-signup" : "#"}
                    className={`block w-full rounded-xl py-3.5 text-center font-bold transition shadow-sm ${
                      tier.available
                        ? "bg-[#45D483] text-white hover:bg-[#22C55E] btn-premium"
                        : "bg-[#F1F5F9] text-[#64748B] cursor-not-allowed"
                    }`}
                  >
                    {tier.available ? "Get Free Beta Access" : "Coming Soon"}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Founder Section */}
        <section className="py-24 bg-white section-fade">
          <div className="mx-auto max-w-6xl px-6">
            <div className="bg-[#2E2E38] rounded-[2.5rem] p-8 md:p-16 text-white overflow-hidden relative">
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#45D483]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

              <div className="grid lg:grid-cols-2 gap-16 relative z-10">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#45D483]/20 text-[#45D483] font-bold text-xs uppercase tracking-wider mb-6">
                    <Target size={12} />
                    Founder Story
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Built by a PC, for PCs.
                  </h2>
                  <div className="space-y-6 text-white/80 leading-relaxed text-lg">
                    <p>
                      "HelloPC wasn't built in Silicon Valley by engineers who've
                      never coached an agent. It was built by a productivity coach
                      who lived the grind every day."
                    </p>
                    <p>
                      "I spent years juggling spreadsheets, sticky notes, and
                      manual CRM logging while trying to scale my coaching
                      practice. I knew there had to be a better way."
                    </p>
                    <div className="flex items-center gap-4 pt-4 mt-4 border-t border-white/10">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#3A7DFF] to-[#2563EB] rounded-full flex items-center justify-center font-bold text-white shadow-lg">
                        CW
                      </div>
                      <div>
                        <div className="font-bold text-white">
                          Cameron Wilson
                        </div>
                        <div className="text-sm text-white/50">
                          Founder & Productivity Coach
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Beta Feedback */}
                <div className="bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10">
                  <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#45D483] animate-pulse"></span>
                    You Build This With Us
                  </h3>
                  <div className="space-y-6">
                    <p className="text-white/90 leading-relaxed">
                      We're not building HelloPC in isolation. Our beta users are shaping what comes next.
                    </p>
                    <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                      <div className="font-bold text-white mb-3">
                        Your feedback drives our roadmap
                      </div>
                      <ul className="space-y-3 text-sm text-white/70">
                        <li className="flex items-start gap-2">
                          <span className="text-[#45D483] mt-0.5">✓</span>
                          <span>Direct access to the founder</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#45D483] mt-0.5">✓</span>
                          <span>Weekly calls with beta users</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#45D483] mt-0.5">✓</span>
                          <span>Vote on what features we build next</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#45D483] mt-0.5">✓</span>
                          <span>Share your coaching workflow — we'll build it</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-8 pt-6 border-t border-white/10">
                    <p className="text-sm text-white/90 text-center font-medium">
                      Beta users shape the product and get lifetime perks when we launch.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 bg-[#F9FAFB] section-fade">
          <div className="mx-auto max-w-4xl px-6 text-center">
            <h2 className="text-4xl font-bold text-[#2E2E38] mb-6">
              Ready to 3X Your Coaching Business?
            </h2>
            <p className="text-xl text-[#2E2E38]/70 mb-10 max-w-2xl mx-auto">
              Join the productivity coaches who are scaling without burning out.
              Get free access during our beta—no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/coach-signup"
                className="bg-[#45D483] text-white px-10 py-4 rounded-full font-bold text-lg shadow-xl shadow-[#45D483]/20 hover:bg-[#22C55E] transition-all hover:-translate-y-1"
              >
                Join the Free Beta
              </Link>
              <a
                href="mailto:help@hellopc.co"
                className="bg-white text-[#2E2E38] border border-slate-200 px-10 py-4 rounded-full font-bold text-lg hover:border-[#3A7DFF] hover:text-[#3A7DFF] transition-colors shadow-sm"
              >
                Talk to the Founder
              </a>
            </div>
            <div className="mt-10 flex flex-wrap justify-center gap-8 text-sm text-slate-400 font-medium">
              <span className="flex items-center gap-2">
                <CheckCircle size={16} className="text-[#45D483]" /> 100% Free
                During Beta
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle size={16} className="text-[#45D483]" /> No Credit
                Card Required
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle size={16} className="text-[#45D483]" /> Direct
                Founder Access
              </span>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-12">
        <div className="mx-auto max-w-7xl px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-1 text-xl font-bold">
            <span className="text-[#3A7DFF]">Hello</span>
            <span className="text-[#45D483]">PC</span>
          </div>
          <div className="flex flex-wrap justify-center gap-8 text-sm font-medium text-slate-600">
            <a href="#features" className="hover:text-[#3A7DFF] transition">
              Features
            </a>
            <a href="#pricing" className="hover:text-[#3A7DFF] transition">
              Pricing
            </a>
            <Link to="/login" className="hover:text-[#3A7DFF] transition">
              Login
            </Link>
            <a
              href="mailto:help@hellopc.co"
              className="hover:text-[#3A7DFF] transition"
            >
              Contact
            </a>
          </div>
          <div className="text-sm text-slate-400">
            &copy; {new Date().getFullYear()} HelloPC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPageNew;
